import { useRef, useEffect } from 'react';
import AgentMessage from './AgentMessage';
import ToolStatusCard from './ToolStatusCard';
import MultipleChoiceCard from './MultipleChoiceCard';
import UserInput from './UserInput';
import type { ChatMessage, ToolStatus, AskUserPrompt } from '../../hooks/useAgentSession';

interface ChatPanelProps {
  messages: ChatMessage[];
  toolStatuses: ToolStatus[];
  askUserPrompt: AskUserPrompt | null;
  isProcessing: boolean;
  isConnected: boolean;
  error: string | null;
  onSendMessage: (content: string) => void;
}

export default function ChatPanel({
  messages,
  toolStatuses,
  askUserPrompt,
  isProcessing,
  isConnected,
  error,
  onSendMessage,
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length, toolStatuses.length, askUserPrompt]);

  return (
    <div className="flex flex-col h-full">
      {/* Connection status */}
      {!isConnected && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-700">
          Connecting to coach...
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Message stream */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {messages.length === 0 && !isProcessing && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Start by telling the coach about the role you're targeting.
          </div>
        )}

        {messages.map((msg) => (
          <AgentMessage
            key={msg.id}
            message={msg}
            isStreaming={msg.id.startsWith('stream-')}
          />
        ))}

        {/* Active tool statuses */}
        {toolStatuses
          .filter((t) => t.status === 'running')
          .map((tool) => (
            <ToolStatusCard key={`${tool.name}-${tool.status}`} tool={tool} />
          ))}

        {/* Ask user prompt */}
        {askUserPrompt && (
          <div className="px-4 py-3 bg-blue-50 border-t border-b border-blue-100">
            <p className="text-sm font-medium text-blue-800 mb-1">
              {askUserPrompt.question}
            </p>
            {askUserPrompt.context && (
              <p className="text-xs text-blue-600 mb-2">{askUserPrompt.context}</p>
            )}
            {askUserPrompt.inputType === 'multiple_choice' && askUserPrompt.choices && (
              <MultipleChoiceCard
                choices={askUserPrompt.choices}
                onSelect={onSendMessage}
              />
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <UserInput
        onSend={onSendMessage}
        disabled={isProcessing}
        showVoice={askUserPrompt?.inputType === 'voice' || !askUserPrompt}
        placeholder={
          askUserPrompt
            ? askUserPrompt.inputType === 'voice'
              ? 'Click the mic or type your answer...'
              : 'Type your answer...'
            : 'Type a message...'
        }
      />
    </div>
  );
}
